import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import {
  calculateLifePurposeAndDirection,
  LifePurposeAnalysis
} from '../../utils/v600/lifePurposeEngine';
import { SpiritualityIntelligenceView } from './SpiritualityIntelligenceView';
import {
  Compass,
  Sparkles,
  Target,
  Layers,
  Flame,
  Shield,
  BookOpen,
  Award,
  ChevronRight,
  ChevronDown,
  Info,
  CheckCircle2,
  AlertTriangle,
  Heart,
  Briefcase,
  Users,
  Sun,
  Zap,
  HelpCircle,
  Clock,
  Calendar,
  Send,
  ArrowRight,
  Sliders,
  Check
} from 'lucide-react';

interface LifePurposeDirectionViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

export const LifePurposeDirectionView: React.FC<LifePurposeDirectionViewProps> = ({
  kundali,
  lang = 'en',
  focusDate,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'blueprint' | 'spirituality' | 'expressions' | 'activation' | 'forensics'>('blueprint');
  const [showGuardrails, setShowGuardrails] = useState<boolean>(false);
  const [showAcharyaEvidence, setShowAcharyaEvidence] = useState<boolean>(false);

  // Compute Chart-Specific 6-Layer Life Purpose & Direction Analysis
  const analysis: LifePurposeAnalysis = useMemo(() => {
    return calculateLifePurposeAndDirection(kundali, lang, { focusDate });
  }, [kundali, lang, focusDate]);

  const {
    primaryTheme,
    secondaryTheme,
    supportingTheme,
    purusharthaProfile,
    elementalDisposition,
    atmakarakaAnalysis: ak,
    amatyakarakaAnalysis: amk,
    dharmaTrikonaConvergence: dt,
    enduringVsCurrent,
    dashaActivation: dasha,
    lifeStage,
    timingWindows
  } = analysis;

  const handleConsultAcharya = () => {
    const prompt = `Please provide deep Vedic Acharya guidance on my Life Purpose & Direction (${primaryTheme.headlineEn}), Atmakaraka ${ak.planet} in House ${ak.house}, Amatyakaraka ${amk.planet}, and active Dasha under ${dasha.activeMahaDasha}-${dasha.activeAntarDasha}.`;
    if (onOpenConsultation) {
      onOpenConsultation(prompt);
    }
  };

  return (
    <div className="space-y-6 text-astro-text">
      {/* =================================================================== */}
      {/* 1. MASTER HEADER & DYNAMIC CHART-DERIVED BANNER                     */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-astro-primary text-white text-xs font-bold uppercase tracking-wider">
                <Compass className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'वैदिक स्वधर्म एवं जीवन उद्देश्य' : 'Vedic Svadharma & Life Purpose'}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white text-astro-text text-xs font-medium border border-astro-border shadow-2xs">
                <Sparkles className="w-3 h-3 text-astro-accent" />
                {isHi ? 'आत्मकारक:' : 'Atmakaraka:'} {ak.planet} ({ak.signEn}) · House {ak.house}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-astro-secondary-surface text-astro-primary text-xs font-bold border border-astro-border">
                <Target className="w-3 h-3 text-astro-primary" />
                {isHi ? purusharthaProfile.dominantDescriptorHi : purusharthaProfile.dominantDescriptorEn}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-900 text-xs font-medium border border-amber-200">
                <Calendar className="w-3 h-3 text-amber-600" />
                {isHi ? `आयु: ${analysis.nativeAge} वर्ष` : `Age: ${analysis.nativeAge}`}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-astro-primary font-serif tracking-tight">
              {isHi ? primaryTheme.headlineHi : primaryTheme.headlineEn}
            </h2>

            <p className="text-xs sm:text-sm text-astro-secondary-text max-w-3xl leading-relaxed italic">
              "{isHi ? primaryTheme.corePurposeHi : primaryTheme.corePurposeEn}"
            </p>

            <div className="pt-2 text-xs text-astro-primary font-mono bg-astro-secondary-surface/70 px-3 py-1.5 rounded-lg border border-astro-border inline-block">
              {primaryTheme.shlokaSanskrit}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
            <button
              onClick={handleConsultAcharya}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isHi ? 'आचार्य से स्वधर्म संवाद' : 'Consult Acharya on Svadharma'}</span>
            </button>
            <div className="text-center text-[10px] text-astro-muted">
              {isHi ? `फोकस दिनांक: ${analysis.focusDateString}` : `Focus Date: ${analysis.focusDateString}`}
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 2. ENDURING NATAL PURPOSE VS CURRENT ACTIVATED DIRECTION BANNER     */}
      {/* =================================================================== */}
      <div className="bg-astro-secondary-surface border border-astro-border rounded-xl p-4 shadow-2xs space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-astro-accent shrink-0" />
          <h3 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
            {isHi ? 'शाश्वत जन्म उद्देश्य बनाम तात्कालिक दशा सक्रियता' : 'Enduring Natal Purpose vs. Current Activated Direction'}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          <div className="p-3.5 rounded-lg bg-white border border-astro-border space-y-1.5">
            <span className="font-bold text-astro-primary block">
              {isHi ? '१. शाश्वत जन्म उद्देश्य (Enduring Natal Blueprint):' : '1. Enduring Natal Blueprint (Lifelong Current):'}
            </span>
            <p className="text-astro-secondary-text leading-relaxed">
              {isHi ? enduringVsCurrent.enduringNatalPurposeHi : enduringVsCurrent.enduringNatalPurposeEn}
            </p>
          </div>

          <div className="p-3.5 rounded-lg bg-white border border-astro-border space-y-1.5">
            <span className="font-bold text-amber-900 block">
              {isHi ? `२. तात्कालिक दशा सक्रियता (${dasha.activeMahaDasha} महादशा):` : `2. Current Activated Direction (${dasha.activeMahaDasha} Mahadasha):`}
            </span>
            <p className="text-astro-secondary-text leading-relaxed">
              {isHi ? enduringVsCurrent.currentDashaActivationHi : enduringVsCurrent.currentDashaActivationEn}
            </p>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 3. CLARITY GUARDRAILS (WHAT THIS IS VS WHAT THIS IS NOT)            */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-xl overflow-hidden shadow-2xs">
        <button
          onClick={() => setShowGuardrails(!showGuardrails)}
          className="w-full px-4 py-3 bg-astro-secondary-surface/70 flex items-center justify-between text-xs font-bold text-astro-text hover:bg-astro-secondary-surface transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-astro-accent" />
            <span>{isHi ? 'वैदिक स्वधर्म की शास्त्रीय मर्यादा (What This IS vs What This is NOT)' : 'Understanding Svadharma: What This IS vs What This is NOT'}</span>
          </div>
          {showGuardrails ? <ChevronDown className="w-4 h-4 text-astro-secondary-text" /> : <ChevronRight className="w-4 h-4 text-astro-secondary-text" />}
        </button>

        {showGuardrails && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white text-xs">
            <div className="p-3.5 rounded-lg bg-emerald-50/70 border border-emerald-200 space-y-2">
              <div className="flex items-center gap-1.5 font-bold text-emerald-900">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>{isHi ? 'यह क्या है (What This IS)' : 'What This IS'}</span>
              </div>
              <ul className="space-y-1.5 text-emerald-950 list-disc list-inside">
                {(isHi ? analysis.guardrails.whatThisIsHi : analysis.guardrails.whatThisIsEn).map((item, idx) => (
                  <li key={idx} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>

            <div className="p-3.5 rounded-lg bg-rose-50/70 border border-rose-200 space-y-2">
              <div className="flex items-center gap-1.5 font-bold text-rose-900">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <span>{isHi ? 'यह क्या नहीं है (What This is NOT)' : 'What This is NOT'}</span>
              </div>
              <ul className="space-y-1.5 text-rose-950 list-disc list-inside">
                {(isHi ? analysis.guardrails.whatThisIsNotHi : analysis.guardrails.whatThisIsNotEn).map((item, idx) => (
                  <li key={idx} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* 4. FIVE INTERACTIVE SECTION TABS                                   */}
      {/* =================================================================== */}
      <div className="flex items-center p-1.5 rounded-xl bg-astro-secondary-surface/70 border border-astro-border gap-1.5 overflow-x-auto custom-horizontal-scrollbar feature-nav-scroll">
        <button
          onClick={() => setActiveTab('blueprint')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
            activeTab === 'blueprint'
              ? 'bg-astro-primary text-white font-bold shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
          }`}
        >
          <Layers className={`w-3.5 h-3.5 ${activeTab === 'blueprint' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
          <span>{isHi ? '१. मूल उद्देश्य व स्वधर्म' : '1. Core Svadharma & Purpose'}</span>
        </button>

        <button
          onClick={() => setActiveTab('spirituality')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
            activeTab === 'spirituality'
              ? 'bg-astro-primary text-white font-bold shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
          }`}
        >
          <Sparkles className={`w-3.5 h-3.5 ${activeTab === 'spirituality' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
          <span>{isHi ? '२. अध्यात्म व आंतरिक साधना' : '2. Spirituality & Inner Path'}</span>
        </button>

        <button
          onClick={() => setActiveTab('expressions')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
            activeTab === 'expressions'
              ? 'bg-astro-primary text-white font-bold shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
          }`}
        >
          <Target className={`w-3.5 h-3.5 ${activeTab === 'expressions' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
          <span>{isHi ? '३. बहु-आयामी अभिव्यक्ति' : '3. Multi-Channel Expressions'}</span>
        </button>

        <button
          onClick={() => setActiveTab('activation')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
            activeTab === 'activation'
              ? 'bg-astro-primary text-white font-bold shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
          }`}
        >
          <Clock className={`w-3.5 h-3.5 ${activeTab === 'activation' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
          <span>{isHi ? '४. दशा सक्रियता व जीवन-चरण' : '4. Dasha Activation & Life Stage'}</span>
        </button>

        <button
          onClick={() => setActiveTab('forensics')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all shrink-0 cursor-pointer ${
            activeTab === 'forensics'
              ? 'bg-astro-primary text-white font-bold shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-primary hover:bg-astro-card border border-transparent'
          }`}
        >
          <BookOpen className={`w-3.5 h-3.5 ${activeTab === 'forensics' ? 'text-astro-accent' : 'text-astro-secondary-text'}`} />
          <span>{isHi ? '५. आचार्य शास्त्रीय साक्ष्य' : '5. Forensic Acharya Evidence'}</span>
        </button>
      </div>

      {/* =================================================================== */}
      {/* TAB 1: CORE PURPOSE & "WHY THIS PERSON?" EVIDENCE                  */}
      {/* =================================================================== */}
      {activeTab === 'blueprint' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Primary Purpose Card */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-astro-secondary-surface text-astro-primary">
                    <Sun className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-astro-primary font-serif">
                      {isHi ? primaryTheme.headlineHi : primaryTheme.headlineEn}
                    </h3>
                    <p className="text-xs text-astro-secondary-text">
                      {isHi ? primaryTheme.sanskritTerm : primaryTheme.sanskritTerm}
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-astro-primary/10 text-astro-primary text-xs font-bold border border-astro-primary/20">
                  {primaryTheme.confidenceDescriptor}
                </span>
              </div>

              {/* WHY THIS PERSON? Traceable Evidence Box */}
              <div className="p-4 rounded-xl bg-white border border-astro-border space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>{isHi ? 'यह आप पर ही क्यों लागू होता है? (Why This Person?)' : 'Why This Applies Specifically to You:'}</span>
                  </div>
                  <button
                    onClick={() => setShowAcharyaEvidence(!showAcharyaEvidence)}
                    className="text-[11px] text-astro-primary underline font-medium hover:text-astro-secondary cursor-pointer"
                  >
                    {showAcharyaEvidence ? (isHi ? 'संक्षिप्त करें' : 'Hide Technical Details') : (isHi ? 'शास्त्रीय विवरण देखें' : 'View Chart Evidence')}
                  </button>
                </div>
                <p className="text-xs text-astro-text leading-relaxed">
                  {isHi ? primaryTheme.whyThisPersonHi : primaryTheme.whyThisPersonEn}
                </p>

                {showAcharyaEvidence && (
                  <div className="mt-3 pt-3 border-t border-astro-border/70 space-y-2">
                    <span className="text-[11px] font-bold text-astro-secondary-text uppercase block">
                      {isHi ? 'कुंडली गणना साक्ष्य:' : 'Calculated Chart Factors:'}
                    </span>
                    <ul className="space-y-1.5 text-xs text-astro-secondary-text">
                      {primaryTheme.evidenceTrail.map((ev, i) => (
                        <li key={i} className="flex items-start gap-2 bg-astro-secondary-surface/60 p-2 rounded-lg">
                          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-semibold text-astro-text">{ev.factor}</span> — {ev.classicalRule}
                            <span className="block text-[10px] text-astro-muted font-mono mt-0.5">{ev.sourceShastra}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Natural Expression & Where It Expresses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3.5 rounded-lg bg-astro-secondary-surface border border-astro-border space-y-1.5">
                  <span className="font-bold text-astro-text block">{isHi ? 'स्वाभाविक प्रकटीकरण:' : 'Natural Everyday Expression:'}</span>
                  <p className="text-astro-secondary-text leading-relaxed">
                    {isHi ? primaryTheme.naturalExpressionHi : primaryTheme.naturalExpressionEn}
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-astro-secondary-surface border border-astro-border space-y-1.5">
                  <span className="font-bold text-astro-text block">{isHi ? 'अनुकूलतम परिवेश:' : 'Environments Where It Thrives:'}</span>
                  <p className="text-astro-secondary-text leading-relaxed">
                    {isHi ? primaryTheme.whereItExpressesHi : primaryTheme.whereItExpressesEn}
                  </p>
                </div>
              </div>

              {/* Strengths & Developmental Challenge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3.5 rounded-lg bg-emerald-50/70 border border-emerald-200 space-y-1.5">
                  <span className="font-bold text-emerald-900 block">{isHi ? 'प्राकृतिक सामर्थ्य (Key Strengths):' : 'Key Natural Strengths:'}</span>
                  <ul className="space-y-1 text-emerald-950 list-disc list-inside">
                    {(isHi ? primaryTheme.keyStrengthsHi : primaryTheme.keyStrengthsEn).map((st, i) => (
                      <li key={i} className="leading-relaxed">{st}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-3.5 rounded-lg bg-amber-50/70 border border-amber-200 space-y-1.5">
                  <span className="font-bold text-amber-900 block">{isHi ? 'विकासात्मक द्वंद्व (Growth Tension):' : 'Developmental Tension / Blindspot:'}</span>
                  <p className="text-amber-950 leading-relaxed">
                    {isHi ? primaryTheme.developmentalTensionHi : primaryTheme.developmentalTensionEn}
                  </p>
                </div>
              </div>

              {/* Tangible Contribution Pattern */}
              <div className="p-3.5 rounded-lg bg-white border border-astro-border text-xs space-y-1">
                <span className="font-bold text-astro-primary block">{isHi ? 'ठोस स्थायी योगदान (Contribution Pattern):' : 'Enduring Contribution Pattern:'}</span>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi ? primaryTheme.contributionPatternHi : primaryTheme.contributionPatternEn}
                </p>
              </div>

              {/* Secondary Theme Preview */}
              <div className="pt-3 border-t border-astro-border flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-astro-accent shrink-0" />
                  <span className="text-astro-secondary-text font-semibold">{isHi ? 'सहायक उद्देश्य धारा:' : 'Secondary Purpose Theme:'}</span>
                  <span className="font-bold text-astro-text">{isHi ? secondaryTheme.headlineHi : secondaryTheme.headlineEn}</span>
                </div>
                <span className="px-2 py-0.5 rounded bg-astro-secondary-surface text-astro-secondary-text border border-astro-border text-[11px] self-start sm:self-auto font-medium">
                  {secondaryTheme.confidenceDescriptor}
                </span>
              </div>
            </div>

            {/* Atmakaraka & Amatyakaraka Soul Instrument Card */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-sm">
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'आत्मकारक एवं अमात्यकारक साधन (Soul & Career Vehicles)' : 'Atmakaraka & Amatyakaraka (Soul Lesson & Action Vehicle)'}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3.5 rounded-lg bg-white border border-astro-border space-y-1">
                  <div className="text-astro-secondary-text text-[11px] font-semibold">{isHi ? 'आत्मकारक ग्रह व भाव' : 'Atmakaraka (Soul Growth Focus)'}</div>
                  <div className="font-bold text-astro-primary text-sm">
                    {ak.planet} in House {ak.house} ({ak.signEn})
                  </div>
                  <div className="text-astro-secondary-text text-[11px]">
                    {isHi ? `कारकांश: ${ak.karakamsaSignHi}` : `Karakamsha: ${ak.karakamsaSignEn}`}
                  </div>
                  <p className="text-astro-text text-xs pt-1 border-t border-astro-border/50">
                    {isHi ? ak.soulLessonHi : ak.soulLessonEn}
                  </p>
                </div>

                <div className="p-3.5 rounded-lg bg-white border border-astro-border space-y-1">
                  <div className="text-astro-secondary-text text-[11px] font-semibold">{isHi ? 'अमात्यकारक (कार्य साधन)' : 'Amatyakaraka (Action Instrument)'}</div>
                  <div className="font-bold text-astro-primary text-sm">
                    {amk.planet} in House {amk.house}
                  </div>
                  <div className="text-astro-secondary-text text-[11px]">
                    {isHi ? 'व्यावहारिक माध्यम:' : 'Practical Expression Vehicle:'}
                  </div>
                  <p className="text-astro-text text-xs pt-1 border-t border-astro-border/50">
                    {isHi ? amk.practicalVehicleHi : amk.practicalVehicleEn}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Purushartha, Elements, Life Stage */}
          <div className="space-y-6">
            {/* Life-Stage Context Card */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                <Calendar className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'आयु व जीवन-चरण परिप्रेक्ष्य' : 'Life-Stage Context (Desh-Kaal-Patra)'}</span>
              </div>
              <div className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                <div className="text-xs font-bold text-astro-primary">
                  {isHi ? lifeStage.stageNameHi : lifeStage.stageNameEn}
                </div>
                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi ? lifeStage.developmentalFocusHi : lifeStage.developmentalFocusEn}
                </p>
              </div>
            </div>

            {/* Qualitative Purushartha Alignment (Zero Pseudo-Precision!) */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                <Compass className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'चार पुरुषार्थ संतुलन' : '4 Purushartha Alignment'}</span>
              </div>

              <div className="space-y-2.5 text-xs">
                <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-astro-border">
                  <span className="font-semibold text-emerald-900">{isHi ? 'धर्म (Dharma - कर्तव्य व सत्य)' : 'Dharma (Duty & Right Action)'}</span>
                  <span className="font-bold text-astro-primary px-2 py-0.5 rounded bg-emerald-50 text-[11px] border border-emerald-200">
                    {purusharthaProfile.dharmaWeight}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-astro-border">
                  <span className="font-semibold text-blue-900">{isHi ? 'अर्थ (Artha - निर्माण व साधन)' : 'Artha (Structure & Creation)'}</span>
                  <span className="font-bold text-astro-primary px-2 py-0.5 rounded bg-blue-50 text-[11px] border border-blue-200">
                    {purusharthaProfile.arthaWeight}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-astro-border">
                  <span className="font-semibold text-purple-900">{isHi ? 'काम (Kama - प्रीति व रचनात्मकता)' : 'Kama (Creativity & Joy)'}</span>
                  <span className="font-medium text-astro-secondary-text px-2 py-0.5 rounded bg-purple-50 text-[11px] border border-purple-200">
                    {purusharthaProfile.kamaWeight}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-astro-border">
                  <span className="font-semibold text-amber-900">{isHi ? 'मोक्ष (Moksha - शांति व मुक्ति)' : 'Moksha (Peace & Transcendence)'}</span>
                  <span className="font-medium text-astro-secondary-text px-2 py-0.5 rounded bg-amber-50 text-[11px] border border-amber-200">
                    {purusharthaProfile.mokshaWeight}
                  </span>
                </div>
              </div>

              <p className="text-xs text-astro-secondary-text leading-relaxed pt-2 border-t border-astro-border">
                {isHi ? purusharthaProfile.summaryHi : purusharthaProfile.summaryEn}
              </p>
            </div>

            {/* Elemental Disposition */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                <Flame className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'तत्व स्वभाव (Elemental Disposition)' : 'Elemental Disposition'}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-secondary-text font-bold uppercase">{isHi ? 'अग्नि (Fire)' : 'Fire (Agni)'}</div>
                  <div className="text-xs font-bold text-astro-text mt-0.5">{elementalDisposition.fireLevel}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-secondary-text font-bold uppercase">{isHi ? 'पृथ्वी (Earth)' : 'Earth (Prithvi)'}</div>
                  <div className="text-xs font-bold text-astro-text mt-0.5">{elementalDisposition.earthLevel}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-secondary-text font-bold uppercase">{isHi ? 'वायु (Air)' : 'Air (Vayu)'}</div>
                  <div className="text-xs font-bold text-astro-text mt-0.5">{elementalDisposition.airLevel}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-secondary-text font-bold uppercase">{isHi ? 'जल (Water)' : 'Water (Jala)'}</div>
                  <div className="text-xs font-bold text-astro-text mt-0.5">{elementalDisposition.waterLevel}</div>
                </div>
              </div>

              <div className="text-xs text-astro-secondary-text">
                <span className="font-semibold text-astro-text">{isHi ? 'प्रधान तत्व:' : 'Dominant Element:'}</span>{' '}
                {isHi ? elementalDisposition.dominantElementHi : elementalDisposition.dominantElementEn}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 2: SPIRITUALITY & INNER PATH (SIME V1.0 MASTER ENGINE)          */}
      {/* =================================================================== */}
      {activeTab === 'spirituality' && (
        <SpiritualityIntelligenceView
          kundali={kundali}
          lang={lang}
          focusDate={focusDate}
          onOpenConsultation={onOpenConsultation}
        />
      )}

      {/* =================================================================== */}
      {/* TAB 3: MULTI-CHANNEL EXPRESSIONS MATRIX                             */}
      {/* =================================================================== */}
      {activeTab === 'expressions' && (
        <div className="space-y-6">
          <div className="bg-astro-card border border-astro-border rounded-xl p-4 flex items-start gap-3 text-xs">
            <Info className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold text-astro-text">
                {isHi ? 'मूल सिद्धांत: जीवन उद्देश्य ≠ केवल एक नौकरी' : 'Core Discipline: Life Purpose != A Single Profession'}
              </span>
              <p className="text-astro-secondary-text leading-relaxed">
                {isHi
                  ? 'शास्त्रानुसार आजीविका केवल एक माध्यम है। आपका उद्देश्य विभिन्न माध्यमों—परामर्श, सेवा, व्यक्तिगत अध्ययन एवं सामाजिक योगदान के द्वारा अभिव्यक्त होता है।'
                  : 'In classical Jyotish, career is merely one functional vehicle. Svadharma expresses across multiple domains simultaneously: leadership, service, craft, learning, and relationships.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {primaryTheme.possibleExpressions.map((expr, idx) => (
              <div
                key={idx}
                className="rounded-xl p-5 border border-astro-border bg-astro-card shadow-2xs space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {idx === 0 ? (
                      <Briefcase className="w-4 h-4 text-astro-primary" />
                    ) : idx === 1 ? (
                      <Layers className="w-4 h-4 text-astro-primary" />
                    ) : idx === 2 ? (
                      <Users className="w-4 h-4 text-astro-accent" />
                    ) : (
                      <Heart className="w-4 h-4 text-astro-accent" />
                    )}
                    <h4 className="font-bold text-sm text-astro-text">
                      {isHi ? expr.channelHi : expr.channelEn}
                    </h4>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      expr.isCareerOnly
                        ? 'bg-astro-secondary-surface text-astro-primary border-astro-border'
                        : 'bg-emerald-50 text-emerald-900 border-emerald-200'
                    }`}
                  >
                    {expr.isCareerOnly ? (isHi ? 'व्यावसायिक' : 'Career Channel') : (isHi ? 'व्यक्तिगत / सेवा' : 'Contribution')}
                  </span>
                </div>

                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi ? expr.descriptionHi : expr.descriptionEn}
                </p>
              </div>
            ))}
          </div>

          {/* Svadharma in Daily Life */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
            <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
              <Zap className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'स्वधर्म का व्यावहारिक क्रियान्वयन' : 'Svadharma in Daily Life'}</span>
            </h4>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi ? analysis.acharyaSynthesis.svadharmaGuidanceHi : analysis.acharyaSynthesis.svadharmaGuidanceEn}
            </p>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 3: DASHA ACTIVATION & PRESENT PHASE                             */}
      {/* =================================================================== */}
      {activeTab === 'activation' && (
        <div className="space-y-6">
          {/* Active Dasha Connection */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-xs space-y-4 text-astro-text">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-astro-accent" />
                <h4 className="font-bold text-base text-astro-primary font-serif">
                  {isHi ? 'वर्तमान दशा द्वारा उद्देश्य का सक्रियण' : 'Active Dasha & Purpose Activation'}
                </h4>
              </div>
              <span className="px-3 py-1 rounded-full bg-astro-secondary-surface text-astro-primary font-mono text-xs font-bold border border-astro-border">
                {dasha.activeMahaDasha} MD / {dasha.activeAntarDasha} AD
              </span>
            </div>

            <p className="text-xs text-astro-secondary-text leading-relaxed">
              {isHi ? dasha.activatedThemeHi : dasha.activatedThemeEn}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-astro-border text-xs">
              <div className="p-3 rounded-lg bg-astro-secondary-surface border border-astro-border space-y-1">
                <span className="text-astro-primary font-bold">{isHi ? 'वर्तमान जीवन पाठ्यक्रम:' : 'Current Growth Focus:'}</span>
                <p className="text-astro-secondary-text leading-relaxed">{isHi ? dasha.currentLifeCurriculumHi : dasha.currentLifeCurriculumEn}</p>
              </div>

              <div className="p-3 rounded-lg bg-astro-secondary-surface border border-astro-border space-y-1">
                <span className="text-astro-primary font-bold">{isHi ? 'सहजता एवं संघर्ष बिंदु:' : 'Friction vs Flow:'}</span>
                <p className="text-astro-secondary-text leading-relaxed">{isHi ? dasha.frictionVsFlowHi : dasha.frictionVsFlowEn}</p>
              </div>
            </div>
          </div>

          {/* Developmental Milestones */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
            <h4 className="font-bold text-sm text-astro-text flex items-center gap-2">
              <Calendar className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'उद्देश्य परिपक्वता कालक्रम (Maturation Horizons)' : 'Purpose Maturation & Timing Horizons'}</span>
            </h4>

            <div className="space-y-3">
              {timingWindows.map((tw, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white border border-astro-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-astro-text">
                        {isHi ? tw.phaseNameHi : tw.phaseNameEn}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 text-[10px] font-mono">
                        {tw.startDate} – {tw.endDate}
                      </span>
                    </div>
                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {isHi ? tw.developmentalFocusHi : tw.developmentalFocusEn}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-astro-secondary-text uppercase font-semibold">{isHi ? 'स्थिति' : 'Status'}</div>
                    <div className="text-xs font-bold text-astro-primary">{isHi ? tw.readinessStatusHi : tw.readinessStatusEn}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 4: FORENSIC ACHARYA EVIDENCE & SHASTRA CITATIONS                */}
      {/* =================================================================== */}
      {activeTab === 'forensics' && (
        <div className="space-y-6">
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
              <BookOpen className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'आचार्य शास्त्रीय साक्ष्य एवं प्रमाण शृंखला' : 'Forensic Shastric Evidence & Citations'}</span>
            </div>

            {/* Classical Shastric Citation */}
            <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2">
              <div className="text-xs font-bold text-astro-text">
                {isHi ? 'शास्त्रीय पाराशरी एवं सारावली उद्धरण:' : 'Classical Shastric Citation:'}
              </div>
              <p className="text-xs text-astro-secondary-text italic leading-relaxed">
                "{isHi ? primaryTheme.classicalCitationHi : primaryTheme.classicalCitationEn}"
              </p>
              <div className="text-[11px] text-astro-secondary-text font-mono">
                {isHi ? analysis.acharyaSynthesis.scriptureReferenceHi : analysis.acharyaSynthesis.scriptureReferenceEn}
              </div>
            </div>

            {/* Evidence Trail Table */}
            <div className="space-y-2">
              <div className="text-xs font-bold text-astro-text">
                {isHi ? 'गणना साक्ष्य एवं श्लोक संदर्भ:' : 'Traceable Evidence Trail:'}
              </div>
              <div className="space-y-2">
                {primaryTheme.evidenceTrail.map((ev, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-white border border-astro-border text-xs space-y-1">
                    <div className="font-bold text-astro-primary">{ev.factor}</div>
                    <p className="text-astro-secondary-text">{ev.classicalRule}</p>
                    <div className="text-[10px] font-mono text-astro-muted">{ev.sourceShastra}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Surgical Upaya */}
            <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200 space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-950">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span>{isHi ? 'स्वधर्म संरेखण हेतु वैदिक उपाय (Surgical Upaya):' : 'Surgical Upayas for Purpose Alignment:'}</span>
              </div>
              <p className="text-xs text-amber-900 leading-relaxed">
                {isHi ? analysis.acharyaSynthesis.upayaHi : analysis.acharyaSynthesis.upayaEn}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
